/**
 * Persistent Customer → Lead → Quote → Invoice → Order navigation strip.
 * Only the focused entity chip expands with details; others stay compact
 * and reveal full details on hover.
 */

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import { buildJobJourneySteps } from "./buildJobJourneySteps.js";

/**
 * @param {{
 *   current: 'customer'|'lead'|'quote'|'invoice'|'order',
 *   customerId?: string|null,
 *   leadId?: string|null,
 *   quoteId?: string|null,
 *   invoiceId?: string|null,
 *   orderId?: string|null,
 *   customerDisplay?: string|null,
 *   leadDisplay?: string|null,
 *   quoteDisplay?: string|null,
 *   invoiceDisplay?: string|null,
 *   orderDisplay?: string|null,
 * }} props
 */
export default function JobJourneyStrip({
  current,
  customerId = null,
  leadId = null,
  quoteId = null,
  invoiceId = null,
  orderId = null,
  customerDisplay = null,
  leadDisplay = null,
  quoteDisplay = null,
  invoiceDisplay = null,
  orderDisplay = null,
}) {
  const navigate = useNavigate();

  const steps = useMemo(
    () =>
      buildJobJourneySteps({
        current,
        customerId,
        leadId,
        quoteId,
        invoiceId,
        orderId,
        customerDisplay,
        leadDisplay,
        quoteDisplay,
        invoiceDisplay,
        orderDisplay,
      }),
    [
      current,
      customerId,
      leadId,
      quoteId,
      invoiceId,
      orderId,
      customerDisplay,
      leadDisplay,
      quoteDisplay,
      invoiceDisplay,
      orderDisplay,
    ]
  );

  const hasAnyLink = steps.some((step) => step.linked);
  if (!hasAnyLink) return null;

  const currentStep = steps.find((s) => s.current);
  const currentSummary = currentStep?.detail
    ? `${currentStep.label} · ${currentStep.detail}`
    : currentStep?.label || "this record";

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 2, px: 2, py: 1.5 }}
      data-testid="job-journey-strip"
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1, letterSpacing: 0.3 }}
      >
        Job journey
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        spacing={0.5}
      >
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 ? (
              <NavigateNext
                fontSize="small"
                sx={{ color: "text.disabled", mx: 0.25 }}
                aria-hidden
              />
            ) : null}
            <Tooltip title={step.tooltip} enterDelay={300} arrow>
              <span>
                <Chip
                  size="small"
                  label={step.chipLabel}
                  color={step.current ? "primary" : "default"}
                  variant={step.current ? "filled" : "outlined"}
                  onClick={
                    step.linked && !step.current && step.path
                      ? () => navigate(step.path)
                      : undefined
                  }
                  clickable={Boolean(step.linked && !step.current && step.path)}
                  sx={{
                    maxWidth: step.current ? 280 : 120,
                    fontWeight: step.current ? 600 : 400,
                    opacity: step.linked || step.current ? 1 : 0.55,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </span>
            </Tooltip>
          </React.Fragment>
        ))}
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.75 }}
      >
        You are on {currentSummary}
      </Typography>
    </Paper>
  );
}
